import { apiIntercom } from '@/api/Intercom'

// 預設合約資料
const defaultContract = () => ({
  SoftContractPeriod: null, // 軟體合約期間
  HardContractPeriod: null, // 硬體合約期間
  // 市話轉接合約期間(包含回撥功能)
  CallForwardingPeriod: {
    StartTime: null,
    EndTime: null,
    CallbackEnabled: false
  },
  PartialSaleContractPeriod: null, // 部分銷售合約期間
  CloudGuestContractPeriod: null // 社區對講機-雲訪客合約
})

// 預設軟體語音合約狀態
const defaultAudioContract = () => ({
  /**
   * 1: 使用中
   * (1) 合約的付費週期是「不計費」
   * (2) 合約的付費週期是「計費」，且合約到期日 > 30 天
   * (3) 合約的付費週期是「計費」，且合約到期日 <= 30 天(即將過期)，但有續約
   * 2: 過期
   * 3: 即將過期(無續約)
   */
  status: 1,
  hasNextContract: false, // 是否有下一個合約(有續約)
  expiresAt: '' // 合約到期日
})

// 預設合約內的統計數據
const defaultContractStats = () => ({
  communityName: '', // 社區名稱
  expiresAt: '', // 合約到期日
  totalCallCount: 0, // 總通話次數
  totalCallDurationInMinutes: 0, // 總通話時間(分鐘)
  averageMonthlyCallCount: 0 // 每月平均通話(次)
})

const state = {
  contract: defaultContract(), // 合約資料
  contractPromise: null, // 合約資料讀取的 promise
  authorizedTabletsTotalNum: 0, // 社區可使用戶數
  audioContract: defaultAudioContract(), // 軟體語音合約狀態
  contractStats: defaultContractStats(), // 合約內的統計數據
  isContractDialogVisible: false, // 是否顯示合約到期提醒對話框
  isContractAlertBannerShow: false, // 是否顯示合約到期提醒橫幅
  isOpenAuthorizedDialog: false // 控制授權名單 dialog 開關
}

const getters = {
  // 是否啟用市話轉接(判斷今天是否在期間內)
  hasCallForwarding: (state) => {
    const { CallForwardingPeriod } = state.contract
    if (!CallForwardingPeriod || !CallForwardingPeriod.StartTime || !CallForwardingPeriod.EndTime) {
      return false
    }
    const today = new Date()
    return today >= new Date(CallForwardingPeriod.StartTime) && today <= new Date(CallForwardingPeriod.EndTime)
  },
  // 是否可回撥
  isCallbackEnabled: (state) => {
    return state.contract.CallForwardingPeriod?.CallbackEnabled || false
  },
  // 是否啟用雲訪客合約(判斷今天是否在期間內)
  hasCloudGuest: (state) => {
    const p = state.contract.CloudGuestContractPeriod
    if (!p?.StartTime || !p?.EndTime) return false
    const today = new Date()
    return today >= new Date(p.StartTime) && today <= new Date(p.EndTime)
  },
  // 是否支援部分銷售
  hasPartialSale: (state) => {
    return !!state.contract.PartialSaleContractPeriod
  },
  // 部分銷售合約資料
  partialSaleContract: (state) => {
    return state.contract.PartialSaleContractPeriod || null
  },
  // 顯示合約到期日期
  displayExpireDate: (state) => {
    const expiresAt = state.audioContract.expiresAt
    if (!expiresAt) return ''
    return expiresAt.split(' ')[0]
  }
}

const mutations = {
  // 設定合約資料
  setContract (state, payload) {
    state.contract = {
      ...state.contract,
      ...payload
    }
    // 設定社區可使用戶數
    state.authorizedTabletsTotalNum = payload.PartialSaleContractPeriod?.NumberOfAuthorizedTablets + payload.PartialSaleContractPeriod?.FreeNumberOfAuthorizedTablets || 0
  },
  // 重置合約資料
  resetContract (state) {
    state.contract = defaultContract()
    state.authorizedTabletsTotalNum = 0
  },
  // 設定合約讀取的 promise
  setContractPromise (state, promise) {
    state.contractPromise = promise
  },
  // 控制授權名單 dialog 顯示狀態
  triggerAuthorizedDialog (state, status) {
    state.isOpenAuthorizedDialog = status
  },
  // 設定軟體語音合約狀態
  setAudioContract (state, payload) {
    state.audioContract = {
      ...state.audioContract,
      ...payload
    }
  },
  // 重置軟體語音合約狀態
  resetAudioContract (state) {
    state.audioContract = defaultAudioContract()
  },
  // 設定合約內的統計數據
  setContractStats (state, payload) {
    state.contractStats = {
      ...state.contractStats,
      ...payload
    }
  },
  // 重置合約內的統計數據
  resetContractStats (state) {
    state.contractStats = defaultContractStats()
  },
  // 設定合約對話框顯示狀態
  setContractDialogVisible (state, status) {
    state.isContractDialogVisible = status
  },
  // 設定合約橫幅顯示狀態
  setContractAlertBannerShow (state, status) {
    state.isContractAlertBannerShow = status
  }
}

const actions = {
  // 取得合約資料
  async getContract ({ state, commit }, forceRefresh = false) {
    // 如果已經有正在進行的 promise 且不強制重新整理，直接返回該 promise
    if (state.contractPromise && !forceRefresh) {
      return state.contractPromise
    }

    // 強制重新整理時，清除舊的 promise
    if (forceRefresh) {
      commit('setContractPromise', null)
    }

    // 建立新的 promise
    const promise = (async () => {
      try {
        const res = await apiIntercom.Sentry.GetContract()
        const { Data } = res?.data || {}
        if (!Data) {
          throw new Error('GetContract: invalid response')
        }
        commit('setContract', {
          SoftContractPeriod: Data.SoftContractPeriod || null,
          HardContractPeriod: Data.HardContractPeriod || null,
          CallForwardingPeriod: {
            StartTime: Data.CallForwardingPeriod?.StartTime || null,
            EndTime: Data.CallForwardingPeriod?.EndTime || null,
            CallbackEnabled: Data.CallForwardingPeriod?.CallbackEnabled || false
          },
          PartialSaleContractPeriod: Data.PartialSaleContractPeriod || null,
          CloudGuestContractPeriod: Data.CloudGuestContractPeriod || null
        })
        return Data
      } catch (error) {
        commit('resetContract')
        throw error
      }
    })()

    // 儲存 promise(保留以便監測是否完成)
    commit('setContractPromise', promise)
    return promise
  },
  // 取得軟體語音合約狀態
  async getAudioContract ({ commit }) {
    try {
      const res = await apiIntercom.Sentry.GetAudioContract()
      const { Data } = res?.data || {}
      if (!Data) {
        throw new Error('GetAudioContract: invalid response')
      }
      commit('setAudioContract', {
        status: Data.Status,
        hasNextContract: Data.HasNextContract,
        expiresAt: Data.ExpiresAt
      })
      return Data
    } catch (error) {
      commit('resetAudioContract')
      throw error
    }
  },
  // 取得合約內的統計數據
  async getContractStatistics ({ commit }) {
    try {
      const res = await apiIntercom.Sentry.GetContractStatistics()
      const { Data } = res?.data || {}
      if (!Data) {
        throw new Error('GetContractStatistics: invalid response')
      }
      commit('setContractStats', {
        communityName: Data.CommunityName,
        expiresAt: Data.ExpiresAt,
        totalCallCount: Data.TotalCallCount,
        totalCallDurationInMinutes: Data.TotalCallDurationInMinutes,
        averageMonthlyCallCount: Data.AverageMonthlyCallCount
      })
      return Data
    } catch (error) {
      commit('resetContractStats')
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
